import {ChangeDetectionStrategy, Component, inject, input, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {Button} from 'primeng/button';
import {Textarea} from 'primeng/textarea';
import {MessageService} from 'primeng/api';

import {SubmitButtonComponent} from '../../shared/components/submit-button/submit-button.component';
import {NotificationsClient} from '../../api-client/api/api';
import {SendNotificationCommandDto} from '../../api-client/model/send-notification-command-dto';

const DEFAULT_PAYLOAD = JSON.stringify(
  {
    type: 'custom',
    data: {
      textMessage: 'Test notification from admin',
      timeSensitive: true,
    },
  },
  null,
  2
);

function hasInformationKey(value: unknown): value is {information: unknown} {
  return typeof value === 'object' && value !== null && 'information' in value;
}

function isRecordWithMessage(value: unknown): value is {message: string} {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  if (!('message' in value)) {
    return false;
  }
  return typeof value.message === 'string';
}

@Component({
  selector: 'homey-send-text-message-page',
  standalone: true,
  imports: [Button, Textarea, SubmitButtonComponent],
  templateUrl: './send-text-message-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendTextMessagePageComponent {
  public readonly userId = input.required<string>();

  private readonly router = inject(Router);
  private readonly notificationsClient = inject(NotificationsClient);
  private readonly messageService = inject(MessageService);

  protected readonly jsonInput = signal(DEFAULT_PAYLOAD);
  protected readonly validationError = signal<string | undefined>(undefined);

  protected onJsonInput(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLTextAreaElement) {
      this.jsonInput.set(target.value);
    }
  }

  protected goBack(): void {
    this.router.navigate(['/users', this.userId()]);
  }

  protected resetJson(): void {
    this.validationError.set(undefined);
    this.jsonInput.set(DEFAULT_PAYLOAD);
  }

  protected readonly sendTextMessage = async (): Promise<void> => {
    this.validationError.set(undefined);

    let parsedPayload: unknown;
    try {
      parsedPayload = JSON.parse(this.jsonInput());
    } catch {
      this.validationError.set('Provided value is not valid JSON.');
      return;
    }

    const command: SendNotificationCommandDto = hasInformationKey(parsedPayload)
      ? {information: parsedPayload.information}
      : {information: parsedPayload};

    try {
      await firstValueFrom(this.notificationsClient.sendUserNotification(this.userId(), command));
      this.messageService.add({
        severity: 'success',
        summary: 'Text message queued',
        detail: 'Notification created successfully.',
      });
      this.resetJson();
    } catch (error) {
      this.validationError.set(this.getErrorMessage(error));
    }
  };

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = this.normalizeServerMessage(error.error);
      if (serverMessage) {
        return serverMessage;
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unable to send text message.';
  }

  private normalizeServerMessage(payload: unknown): string | undefined {
    if (typeof payload === 'string' && payload.length > 0) {
      return payload;
    }

    if (isRecordWithMessage(payload)) {
      return payload.message;
    }

    return undefined;
  }
}
